import { orderBy } from "lodash";
import { Acl, SalesPerson, SearchEntityTypeEnum, UserBasicEntity } from "view360Card/models";
import { OfwcaProfile } from "view360Card/models/OfwcaProfile";
import { Training } from "view360Card/models/Training";
import { OfwcaProfileDto } from "view360Card/service/dtos/OfwcaProfileDto";
import { SalesPersonDto } from "view360Card/service/dtos/SalesPersonDto";
import { TrainingDto } from "view360Card/service/dtos/TrainingDto";
import { DynamicAttributeFromResponse, sortAndFilterDynamicAttributes } from "./DynamicAttributeFromResponse";
import { DictionaryItemFromResponse } from "./DictionaryItemFromResponse";
import { UserBasicEntityDto } from "view360Card/service/dtos/UserBasicEntityDto";
import { ProfileIdentityDto } from "view360Card/service/dtos/ProfileIdentityDto";
import { ProfileIdentity } from "view360Card/models/ProfileIdentity";
import { AclDto } from "view360Card/service/dtos/AclDto";

export class SalesPersonFromResponse {
    constructor(private dto: SalesPersonDto) {}

    salesPerson(): SalesPerson {
        return {
            id: this.dto.id,
            punu: this.dto.punu,
            status: new DictionaryItemFromResponse(this.dto.status).domainModel(),
            birthDate: this.dto.birthDate ? new Date(this.dto.birthDate) : undefined,
            firstName: this.dto.firstName,
            lastName: this.dto.lastName,
            name: `${this.dto.firstName ?? ""} ${this.dto.lastName ?? ""}`,
            systemNumber: this.dto.systemNumber,
            emailEHM: this.dto.emailEHM,
            emailEHZ: this.dto.emailEHZ,
            phoneNumberEHM: this.dto.phoneNumberEHM,
            phoneNumberEHZ: this.dto.phoneNumberEHZ,
            marketingConsentEHM: this.dto.marketingConsentEHM,
            marketingConsentEHZ: this.dto.marketingConsentEHZ,
            knfDateEHM: this.dto.knfDateEHM ? new Date(this.dto.knfDateEHM) : undefined,
            knfDateEHZ: this.dto.knfDateEHZ ? new Date(this.dto.knfDateEHZ) : undefined,
            ipunktStatus: this.dto.ipunktStatus,
            certification: new DictionaryItemFromResponse(this.dto.certification).domainModel(),
            trainingList: this.sortTrainings(
                (this.dto.trainingList ?? []).map((i) => {
                    return new TrainingFromResponse(i).domainModel();
                })
            ),
            profileList: this.sortProfiles(
                (this.dto.profileList ?? []).map((i) => {
                    return new OfwcaProfileFromResponse(i).domainModel();
                })
            ),
            dynamicAttributeList: sortAndFilterDynamicAttributes(
                (this.dto.dynamicAttributeList ?? []).map((i) => {
                    return new DynamicAttributeFromResponse(i).domainModel();
                })
            ),
            businessIdentifier: this.dto.punu,
            acl: new EntityAclFromResponse(this.dto.acl).domainModel(),
            entityType: SearchEntityTypeEnum.SalesPerson,

        };
    }

    private sortTrainings(trainings: Training[]) {
        return orderBy(
            trainings,
            [
                (training) => training.isSummary && training.tenantCode === "EHM",
                (training) => training.isSummary && training.tenantCode === "EHZ",
                (training) => training.trainingDate,
            ],
            ["desc", "desc", "desc"]
        );
    }

    private sortProfiles(profiles: OfwcaProfile[]) {
        return orderBy(
            profiles,
            [
                (profile) => profile.isOwnNetwork === true,
                (profile) => profile.isIndependentNetwork === true,
                (profile) => profile.isDomesticMultiagency === true,
                (profile) => profile.channel?.toLowerCase(),
            ],
            ["desc", "desc", "desc", "asc"]
        );
    }
}

export class TrainingFromResponse {
    constructor(private dto: TrainingDto) {}

    domainModel(): Training {
        const trainingDate = this.dto.trainingDate ? new Date(this.dto.trainingDate) : undefined;
        return {
            id: this.dto.id,
            tenantCode: this.dto.tenantCode,
            sourceCode: this.dto.sourceCode,
            sourceTrainingId: this.dto.sourceTrainingId,
            trainingDate: trainingDate,
            trainingName: this.dto.trainingName,
            type: new DictionaryItemFromResponse(this.dto.type).domainModel(),
            hoursCount: this.dto.hoursCount,
            workTraining: this.dto.workTraining,
            completionFlag: this.dto.completionFlag,
            isSummary: trainingDate ? trainingDate.getUTCMonth() === 11 && trainingDate.getUTCDate() === 31 : false,
        };
    }
}

export class UserBasicEntityFromResponse {
    constructor(private dto: UserBasicEntityDto | undefined) {}

    domainModel(): UserBasicEntity | undefined {
        if (!this.dto) {
            return undefined;
        }

        return {
            username: this.dto.username,
            firstName: this.dto.firstName,
            lastName: this.dto.lastName,
            fullName: this.dto.fullName,
            mobilePhone: this.dto.mobilePhone,
            userPrincipalName: this.dto.userPrincipalName,
        };
    }
}

export class ProfileIdentityFromResponse {
    constructor(private dto: ProfileIdentityDto) {}

    domainModel(): ProfileIdentity {
        return {
            number: this.dto.number,
            isMain: this.dto.isMain,
        }
    }
}

export class EntityAclFromResponse {
    constructor(private dto: AclDto | undefined) {}

    domainModel(): Acl | undefined {
        if (!this.dto) {
            return undefined;
        }

        return {
            commissionTable: this.dto.commissionTable,
            contracts: this.dto.contracts,
            employees: this.dto.employees,
            salesReports: this.dto.salesReports,
            notes: this.dto.notes,
        };
    }
}

export class OfwcaProfileFromResponse {
    constructor(private dto: OfwcaProfileDto) {}

    private wlasnaSiecAgencyjna: string = "własna sieć agencyjna";
    private niezaleznaSiecAgencyjna: string = "niezależna sieć agencyjna";
    private multiagencjeKrajowe: string = "multiagencje krajowe";

    domainModel(): OfwcaProfile {
        return {
            name: this.dto.name,
            number: this.dto.number,
            branch: this.dto.branch,
            role: this.dto.role,
            channel: this.dto.channel,
            systemAccess: this.dto.systemAccess,
            director: new UserBasicEntityFromResponse(this.dto.director).domainModel(),
            rms: new UserBasicEntityFromResponse(this.dto.rms).domainModel(),
            isOwnNetwork: this.dto.channel?.toLocaleLowerCase() === this.wlasnaSiecAgencyjna,
            isIndependentNetwork: this.dto.channel?.toLocaleLowerCase() === this.niezaleznaSiecAgencyjna,
            isDomesticMultiagency: this.dto.channel?.toLocaleLowerCase() === this.multiagencjeKrajowe,
            intermediaryId: this.dto.intermediaryId,
            profileIdentityList: this.sortProfileIdentities(
                (this.dto.profileIdentityList ?? []).map((i) => {
                    return new ProfileIdentityFromResponse(i).domainModel();
                })
            ),
        };
    }

    private sortProfileIdentities(identities: ProfileIdentity[]) {
        return orderBy(identities, [(i) => i.isMain], ["desc"]);
    }
}
